import { Type } from '@sinclair/typebox';
import { IsoDateTimeSchema, UuidSchema } from './common.js';
import { TrackLevelSchema } from './track-roles.js';
// Wire format for the hub's Zanzibar-style authorization endpoints.
//
// Object refs are always direct tuples (`"<type>:<id>"`). Subject refs may be
// direct or userset-shaped (`"<type>:<id>#<relation>"`) so the admin surface
// can round-trip stored policy tuples without losing information.
//
// The ref regexes are exported so downstream validators (e.g. the hub-mcp zod
// tool schemas) derive from the same single literal instead of re-encoding it
// and drifting — same one-literal discipline as `uuidRegex` in `common.ts`.
export const OBJECT_REF_PATTERN = '^[A-Za-z0-9_]+:[^#]+$';
export const SUBJECT_REF_PATTERN = '^[A-Za-z0-9_]+:[^#]+(#[A-Za-z0-9_]+)?$';
const ObjectRefString = Type.String({
    minLength: 3,
    maxLength: 256,
    pattern: OBJECT_REF_PATTERN,
    description: 'Reference of the form "<type>:<id>".',
});
const SubjectRefString = Type.String({
    minLength: 3,
    maxLength: 256,
    pattern: SUBJECT_REF_PATTERN,
    description: 'Reference of the form "<type>:<id>" or "<type>:<id>#<relation>".',
});
export const TupleSchema = Type.Object({
    object: ObjectRefString,
    relation: Type.String({ minLength: 1, maxLength: 64 }),
    subject: SubjectRefString,
});
// POST /permissions/check
export const CheckBodySchema = TupleSchema;
export const CheckResponseSchema = Type.Object({
    allowed: Type.Boolean(),
});
// POST /permissions/check-batch
export const CheckBatchBodySchema = Type.Object({
    items: Type.Array(TupleSchema, { minItems: 1, maxItems: 100 }),
});
export const CheckBatchResponseSchema = Type.Object({
    results: Type.Array(CheckResponseSchema),
});
// POST /permissions/list-objects
export const ListObjectsBodySchema = Type.Object({
    subject: SubjectRefString,
    objectType: Type.String({ minLength: 1, maxLength: 64 }),
    relation: Type.String({ minLength: 1, maxLength: 64 }),
});
export const ListObjectsResponseSchema = Type.Object({
    // '*' indicates the subject is allowed every object of this type
    // (typically: system#admin). Callers should treat '*' as "no filter".
    objectIds: Type.Union([Type.Array(Type.String()), Type.Literal('*')]),
});
// POST /relations  +  DELETE /relations  → 204 No Content (TupleSchema body, no response body)
export const RelationBodySchema = TupleSchema;
export const RelationListQuerySchema = Type.Object({
    object: Type.Optional(ObjectRefString),
    objectType: Type.Optional(Type.String({ minLength: 1, maxLength: 64 })),
    objectId: Type.Optional(Type.String({ minLength: 1, maxLength: 256 })),
    relation: Type.Optional(Type.String({ minLength: 1, maxLength: 64 })),
    subject: Type.Optional(SubjectRefString),
    subjectType: Type.Optional(Type.String({ minLength: 1, maxLength: 64 })),
    subjectId: Type.Optional(Type.String({ minLength: 1, maxLength: 256 })),
    subjectRelation: Type.Optional(Type.String({ minLength: 1, maxLength: 64 })),
    limit: Type.Optional(Type.Integer({ minimum: 1, maximum: 500 })),
    offset: Type.Optional(Type.Integer({ minimum: 0 })),
    // Structural containment tuples (`parent`) are the object graph, not grants
    // a person made — they outnumber real grants ~16:1 in a live hub. Default
    // TRUE so existing callers keep seeing every row; the admin list opts out.
    includeStructural: Type.Optional(Type.Boolean()),
    // Free-text search across BOTH sides of the tuple: matches a ref's resolved
    // label (`Q3 Pricing.pdf`, `bob@example.com`) or a substring of its raw id,
    // in any namespace. ANDs with the other filters, so `objectType` narrows it.
    q: Type.Optional(Type.String({ minLength: 1, maxLength: 128 })),
});
export const RelationListItemSchema = Type.Object({
    id: UuidSchema,
    object: ObjectRefString,
    relation: Type.String({ minLength: 1, maxLength: 64 }),
    subject: SubjectRefString,
    createdAt: IsoDateTimeSchema,
    // Human labels resolved from each ref's owning table at read time — never
    // stored on the tuple, so a rename can't leave a stale copy here. Absent
    // when the namespace has no label source (`audit_log`), when the type isn't
    // a hub namespace at all (`estimate`), or when the referent is gone.
    objectLabel: Type.Optional(Type.String()),
    subjectLabel: Type.Optional(Type.String()),
});
export const RelationListResponseSchema = Type.Object({
    relations: Type.Array(RelationListItemSchema),
    total: Type.Integer({ minimum: 0 }),
    limit: Type.Integer({ minimum: 1, maximum: 500 }),
    offset: Type.Integer({ minimum: 0 }),
    // Present only when `q` matched more objects in one namespace than the
    // search cap. Surfaced rather than swallowed: a capped result set that
    // looks complete is worse than no search at all.
    searchTruncated: Type.Optional(Type.Boolean()),
});
// POST /relations/batch
export const RelationBatchBodySchema = Type.Object({
    writes: Type.Optional(Type.Array(TupleSchema, { maxItems: 200 })),
    deletes: Type.Optional(Type.Array(TupleSchema, { maxItems: 200 })),
});
export const RelationBatchResponseSchema = Type.Object({
    written: Type.Integer(),
    deleted: Type.Integer(),
});
// HUB-033 — customer-facing tuple management, mounted at
// /objects/:objectType/:objectId/*. Track admins self-manage their own
// track's admin/member/viewer tuples (HUB-069 declared-direct set); system
// admins bypass via the engine short-circuit. Subjects are pinned to
// `user:<uuid>` on this surface — free-form subjects stay on the
// system-admin /relations family.
export const TrackScopedObjectParamsSchema = Type.Object({
    objectType: Type.String({ minLength: 1, maxLength: 64 }),
    objectId: Type.String({ minLength: 1, maxLength: 256 }),
});
// GET /objects/:objectType/:objectId/relations
export const TrackScopedRelationsListQuerySchema = Type.Object({
    relation: Type.Optional(Type.String({ minLength: 1, maxLength: 64 })),
    limit: Type.Optional(Type.Integer({ minimum: 1, maximum: 500 })),
    offset: Type.Optional(Type.Integer({ minimum: 0 })),
});
export const TrackScopedRelationsListResponseSchema = Type.Object({
    items: Type.Array(RelationListItemSchema),
    total: Type.Integer({ minimum: 0 }),
});
// GET /objects/:objectType/:objectId/users — HUB-109 D7: each user carries
// the highest DIRECT level (`null` when only a role admits them) and the
// track roles they are a member of; a role-only user is listed too.
export const TrackScopedUsersListResponseSchema = Type.Object({
    items: Type.Array(Type.Object({
        userId: UuidSchema,
        email: Type.String(),
        name: Type.Union([Type.String(), Type.Null()]),
        relations: Type.Array(Type.String()),
        level: Type.Union([TrackLevelSchema, Type.Null()]),
        roles: Type.Array(Type.Object({ id: UuidSchema, name: Type.String() })),
    })),
    total: Type.Integer({ minimum: 0 }),
});
// GET /objects/:objectType/:objectId/role-summary
export const TrackScopedRoleSummaryResponseSchema = Type.Object({
    items: Type.Array(Type.Object({
        relation: Type.String(),
        userCount: Type.Integer({ minimum: 0 }),
    })),
});
// POST /objects/:objectType/:objectId/relations — HUB-109 D7: a level
// (`relation`) and/or role memberships (`roleIds`); at least one is required
// (the handler answers 400 `missing_grant` when both are absent).
export const TrackScopedCreateRelationBodySchema = Type.Object({
    relation: Type.Optional(Type.String({ pattern: '^[a-z][a-z0-9_-]*$', maxLength: 64 })),
    roleIds: Type.Optional(Type.Array(UuidSchema, { maxItems: 50 })),
    subjectType: Type.Literal('user'),
    subjectId: UuidSchema,
});
// DELETE /objects/:objectType/:objectId/relations (body-carried, same
// precedent as DELETE /relations)
export const TrackScopedDeleteRelationBodySchema = Type.Object({
    relation: Type.Optional(Type.String({ pattern: '^[a-z][a-z0-9_-]*$', maxLength: 64 })),
    roleIds: Type.Optional(Type.Array(UuidSchema, { maxItems: 50 })),
    subjectType: Type.Literal('user'),
    subjectId: UuidSchema,
});
// GET /objects/:objectType/:objectId/user-search?email=
export const TrackScopedUserSearchQuerySchema = Type.Object({
    email: Type.String({ minLength: 2, maxLength: 256 }),
});
// Items carry exactly { id, email, name } — NEVER `roles` (HUB-033: this
// surface exists so track admins don't need GET /users).
export const TrackScopedUserSearchResponseSchema = Type.Object({
    items: Type.Array(Type.Object({
        id: UuidSchema,
        email: Type.String(),
        name: Type.Union([Type.String(), Type.Null()]),
    })),
});
// GET /relations/namespaces — direct-relation surface per known object type.
// Drives the admin SPA's relation dropdown so operators don't type relations
// (e.g. `track#write`) that the authz engine never consults. See P-41.3.
export const RelationNamespacesResponseSchema = Type.Object({
    namespaces: Type.Record(Type.String(), Type.Object({
        directRelations: Type.Array(Type.String()),
    })),
});
// GET /relations/objects — labelled objects of ONE namespace, for the tuple
// form's object/subject pickers. Search runs over the same label columns the
// list resolves labels from, so what an operator searches is what they see.
export const RelationObjectsQuerySchema = Type.Object({
    type: Type.String({ minLength: 1, maxLength: 64 }),
    q: Type.Optional(Type.String({ maxLength: 128 })),
    limit: Type.Optional(Type.Integer({ minimum: 1, maximum: 100 })),
});
export const RelationObjectsResponseSchema = Type.Object({
    objects: Type.Array(Type.Object({ id: Type.String(), label: Type.String() })),
    // False when the namespace has no name to search (`system`, `audit_log`),
    // when its labels are derived (`chunk`), or when the type is not a namespace
    // at all (`estimate` is a legal subject type). The caller must then let the
    // operator type a raw id — those refs are still writable.
    searchable: Type.Boolean(),
});
//# sourceMappingURL=permissions.js.map